import React, { useEffect, useState } from 'react';
import { adminApi } from '../api';

export default function AdminQuota() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    freeUserLimit: 5,
    trialUserLimit: 5,
    paidUserLimit: -1,
    enabled: true
  });

  const loadConfig = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await adminApi.getQuotaConfig();
      setConfig(data);
      setForm({
        freeUserLimit: data.freeUserLimit || 5,
        trialUserLimit: data.trialUserLimit || 5,
        paidUserLimit: data.paidUserLimit || -1,
        enabled: data.enabled !== false
      });
    } catch (e) {
      setError(e?.message || 'Failed to load quota configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await adminApi.updateQuotaConfig(form);
      setSuccess('Quota configuration updated successfully');
      await loadConfig();
    } catch (e) {
      setError(e?.message || 'Failed to update quota configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({
      freeUserLimit: config?.freeUserLimit || 5,
      trialUserLimit: config?.trialUserLimit || 5,
      paidUserLimit: config?.paidUserLimit || -1,
      enabled: config?.enabled !== false
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading quota configuration...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Quota Management</h1>
        <p className="text-gray-600">Configure user quotas for WhatsApp and other features</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="text-green-800">{success}</div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Global Quota Settings</h2>
          
          <div className="space-y-6">
            {/* Enable/Disable Quota System */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Quota System</h3>
                <p className="text-sm text-gray-600">Enable or disable the quota system globally</p>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Enabled</span>
              </label>
            </div>

            {/* Free Users Quota */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Free Users Limit
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.freeUserLimit}
                  onChange={(e) => setForm({ ...form, freeUserLimit: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="5"
                />
                <p className="text-xs text-gray-500 mt-1">Total entries for free users</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trial Users Limit
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.trialUserLimit}
                  onChange={(e) => setForm({ ...form, trialUserLimit: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="5"
                />
                <p className="text-xs text-gray-500 mt-1">Total entries for trial users</p>
              </div>
            </div>

            {/* Paid Users Quota */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paid Users Limit
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="-1"
                  value={form.paidUserLimit === -1 ? '' : form.paidUserLimit}
                  onChange={(e) => setForm({ ...form, paidUserLimit: parseInt(e.target.value) || -1 })}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Unlimited"
                />
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={form.paidUserLimit === -1}
                    onChange={(e) => setForm({ ...form, paidUserLimit: e.target.checked ? -1 : 100 })}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Unlimited</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Total entries for paid users (-1 for unlimited)
              </p>
            </div>
          </div>
        </div>

        {/* Current Configuration Display */}
        {config && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Current Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Free Users:</span>
                <span className="ml-2 text-blue-700">{config.freeUserLimit} total entries</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Trial Users:</span>
                <span className="ml-2 text-blue-700">{config.trialUserLimit} total entries</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Paid Users:</span>
                <span className="ml-2 text-blue-700">
                  {config.paidUserLimit === -1 ? 'Unlimited' : `${config.paidUserLimit} total entries`}
                </span>
              </div>
            </div>
            <div className="mt-2 text-sm">
              <span className="font-medium text-blue-800">Status:</span>
              <span className={`ml-2 ${config.enabled ? 'text-green-700' : 'text-red-700'}`}>
                {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {config.updatedAt && (
              <div className="mt-2 text-xs text-blue-600">
                Last updated: {new Date(config.updatedAt).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={saving}
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {/* Information Panel */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-yellow-800 mb-2">ℹ️ How Quotas Work</h3>
        <div className="text-sm text-yellow-700 space-y-2">
          <p>• <strong>Free Users:</strong> Limited to the specified number of total entries</p>
          <p>• <strong>Trial Users:</strong> Limited to the specified number of total entries</p>
          <p>• <strong>Paid Users:</strong> Can be unlimited (-1) or have a specific limit</p>
          <p>• <strong>Quota Tracking:</strong> All quotas track total lifetime usage</p>
          <p>• <strong>WhatsApp Integration:</strong> Quotas apply to WhatsApp bot interactions</p>
          <p>• <strong>Individual Overrides:</strong> You can set custom quotas for specific users</p>
        </div>
      </div>
    </div>
  );
}
